import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authenticationService, crmService, invitationService, profileService } from '../../services';
import {
  CustomFieldResponses,
  InviteType,
  CustomFieldType,
  CrmInviteData,
  DashboardMemberFieldType,
} from '../../types/invites';
import { ProfileDataType, ProfileField } from '../../types/profile';
import { createEmptyCustomFieldResponses, createEmptyField } from '../../utils/formatUtils';
import { sortProfileFields } from '../../utils/sortUtils';
import { UserAccountCreationEvent, UserAccountCreationChannel } from '../../analytics/models';
import { SharedBusinessData } from '../../types/misc';
import { LoginPageState, SuccessPageState } from '../../types/navigation';
import { isEmptyField, isValidProfileFields, checkMandatoryFields } from '../../utils/validationUtils';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import CommunicationNameInput from '../../components/forms/profile/CommunicationNameInput';
import EmailInput from '../../components/forms/profile/EmailInput';
import NameInput from '../../components/forms/profile/NameInput';
import ProfileFieldForm from '../../components/forms/profile/ProfileFieldForm';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import Button from '../../components/misc/Button';
import TermsAndConditions from '../../components/misc/TermsAndConditions';
import AccountExistsModal from '../../components/modals/AccountExistsModal';

export default function UnauthenticatedCrmUserInvitePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const token = params.get('invitationId');
  const link = params.get('link');

  if (link) authenticationService.loginMagic(link);

  const [invite, setInvite] = useState<CrmInviteData>();
  const [alias, setAlias] = useState<string>('');
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>(
    createEmptyCustomFieldResponses([]),
  );
  const [validate, setValidate] = useState<boolean>(false);

  const [accountExists, setAccountExists] = useState<boolean>(false);
  const [accountExistsModal, setAccountExistsModal] = useState<boolean>(false);
  const [terms, setTerms] = useState<boolean>(false);

  const emailField = profileFields.find((x) => x.dataType === ProfileDataType.EMAIL);

  useEffect(() => {
    if (!token) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
    }
  }, []);

  useLayoutEffect(() => {
    if (!token || link) return;

    const setFields = async () => {
      const { networkInvite }: LoginPageState = location.state ?? { networkInvite: undefined };

      const inv = (networkInvite ||
        (await invitationService.getCrmUserInvite(token).catch(() => {
          toast.error(t('toast.error.general.inviteNotFound'));
          navigate('/');
        }))) as CrmInviteData;
      if (!inv) return;
      setInvite(inv);

      const { mandatoryFields = [], optionalFields, customFields, receivedData, alias: aliasInvite } = inv;

      const dataRequest = Array.from(new Set([...mandatoryFields, ...optionalFields, DashboardMemberFieldType.EMAIL]));
      setProfileFields(
        sortProfileFields(
          dataRequest
            .map(
              (f) => receivedData?.find((x) => x.dataType === f || x.social?.socialType === f) || createEmptyField(f),
            )
            .filter((x) => !!x) as ProfileField[],
        ),
      );
      setAlias(aliasInvite);
      setCustomFieldResponses(createEmptyCustomFieldResponses(customFields));
    };

    setFields();
  }, []);

  const validateFields = (): boolean => {
    if (!alias || !alias.split('+')[0] || !alias.split('+')[1]) {
      toast.error(t('toast.error.field.name'));
      return false;
    }
    if (!terms) {
      toast.error(t('toast.error.general.terms'));
      return false;
    }
    if (isEmptyField(profileFields.find((x) => x.dataType === ProfileDataType.EMAIL)!)) {
      toast.error(t('toast.error.field.email'));
      return false;
    }
    return isValidProfileFields(profileFields);
  };

  const handleSave = async () => {
    if (!invite || !token) return;
    setValidate(true);
    if (!validateFields()) return;
    if (!checkMandatoryFields(invite!, profileFields, customFieldResponses)) return;
    if (accountExists) {
      setAccountExistsModal(true);
      return;
    }

    const event: UserAccountCreationEvent = {
      channel: UserAccountCreationChannel[UserAccountCreationChannel.CRM_USER_INVITE],
      details: undefined,
    };
    const login = await authenticationService.register(alias, emailField?.email!, event);

    const properties = await profileService.addFields(
      profileFields.filter((x) => !isEmptyField(x) && x.dataType !== ProfileDataType.EMAIL),
    );

    const args: SharedBusinessData = {
      propertyIds: properties
        .filter((f) => f.dataType !== ProfileDataType.EMAIL || !invite.nonRequestedFields.includes(f.dataType))
        .map((x) => x.id!),
      customFieldResponses,
      dataForMembers: [],
    };

    await crmService.acceptCrmUserInvite(token, {
      propertyIds: args.propertyIds.map((x) => +x),
      customFieldResponses: args.customFieldResponses,
      dataForMembers: [],
    });

    const state: SuccessPageState = {
      translationKey: 'page.common.success.message.businessCommunityInvite',
      translationOptions: {
        businessName: invite.name,
      },
      networkInvite: invite,
    };

    if (invite.communityId && invite.communityFeatures?.EASY_SHARING)
      navigate(`/communities/${invite.communityId}/members`, { state: { invite, success: state, replace: true } });
    else navigate('/success', { state });

    setTimeout(login, 1);
  };

  const handleLogin = async () => {
    const state: LoginPageState = {
      email: emailField?.email! || '',
      invitationId: token!,
      inviteType: InviteType.CRM_USER_INVITE,
      networkInvite: invite!,
    };
    navigate('/login', { state });
  };

  const setProfileFieldByType = (field: ProfileField) => {
    if (field.dataType === ProfileDataType.SOCIAL) {
      setProfileFields(
        profileFields.map(
          (f: ProfileField): ProfileField => (f.social?.socialType === field.social?.socialType ? field : f),
        ),
      );
    } else
      setProfileFields(
        profileFields.map((f: ProfileField): ProfileField => (f.dataType === field.dataType ? field : f)),
      );
  };

  const communicationName = invite?.customFields.find((f) => f.type === CustomFieldType.COMMUNICATION_NAME);

  return (
    <>
      <ResponsePageWrapper handleLogin={handleLogin}>
        <div className="flex flex-col">
          <h2 className="text-lg font-medium">
            {!invite?.dashboardFeatures?.MEMBER_ADMINISTRATION && invite?.communityFeatures?.EASY_SHARING
              ? t('page.businessInvite.description', { communityName: invite?.name })
              : t('page.shared.requestedData')}
          </h2>
          <p className="text-sm">
            {!invite?.dashboardFeatures?.MEMBER_ADMINISTRATION && invite?.communityFeatures?.EASY_SHARING
              ? t('page.businessInvite.mandatory')
              : t('page.businessInvite.message', { name: invite?.name })}
          </p>
        </div>
        <div className="mt-4 flex flex-col gap-4">
          <NameInput alias={alias} setAlias={setAlias} mandatory validate={validate} />

          {communicationName && (
            <CommunicationNameInput
              name={customFieldResponses[communicationName.customFieldId]}
              validate={validate}
              setName={(name) =>
                setCustomFieldResponses({
                  ...customFieldResponses,
                  [communicationName.customFieldId]: name,
                })
              }
              mandatory={communicationName?.mandatory}
            />
          )}

          {emailField && (
            <EmailInput
              field={emailField!}
              emailCheck
              validate={validate}
              mandatory
              setAccountExists={setAccountExists}
              setField={setProfileFieldByType}
            />
          )}
          {accountExists ? (
            <span className="text-sm text-error">
              {t('page.shared.emailInUse')}{' '}
              <strong className="cursor-pointer underline" onClick={handleLogin}>
                {t('page.shared.logInHere')}
              </strong>
              .
            </span>
          ) : (
            ''
          )}

          {profileFields.map((field) => {
            return field.dataType === ProfileDataType.EMAIL ? (
              <></>
            ) : (
              <ProfileFieldForm
                field={field}
                canBeEmpty={!invite?.mandatoryFields.includes(field.dataType)}
                validate={validate}
                mandatory={invite?.mandatoryFields.includes(field.dataType)}
                setField={setProfileFieldByType}
              />
            );
          })}
          <CustomFieldsForm
            fields={invite?.customFields || []}
            response={customFieldResponses}
            setResponse={setCustomFieldResponses}
          />
        </div>
        <div className="mt-4">
          <TermsAndConditions checked={terms} setChecked={setTerms} />
        </div>
        <div className="flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
          <Button variant="primary" className="px-[32px]" onClick={handleSave}>
            {t('page.shared.acceptInvite')}
          </Button>
        </div>
      </ResponsePageWrapper>
      {emailField?.email && token && (
        <AccountExistsModal
          open={accountExistsModal}
          setOpen={setAccountExistsModal}
          request={{
            inviteType: InviteType.CRM_USER_INVITE,
            invitationId: token,
            email: emailField.email,
          }}
        />
      )}
    </>
  );
}
